import { useCallback, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import classNames from 'classnames';
import {
  OfferType,
  useOffers,
  usePlanOffers,
  useSendAction,
  useSession,
  useAddOfferToPlan,
  Offer,
  Product,
  SessionStatus,
  usePlan,
} from '@app/api';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { filterOffers } from '../../../StudySetup/GeneralSetup/filterOffers';
import {
  Button,
  CenteredModal,
  CompactSupportOfferBlock,
  useModalState,
} from 'components';
import { getSupportOfferPrice } from 'lib/planAndOffer';

interface Props {
  sessionId?: string;
  className?: string;
}

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    backgroundColor: theme.colors.blueMuted,
    padding: theme.marginBase * 2,
    borderRadius: theme.borderRadius,
  },
  title: {
    ...theme.fonts.label,
  },
  description: {
    ...theme.fonts.caption,
  },
  modalText: {
    ...theme.fonts.body,
    marginTop: theme.marginBase,
  },
  suportContainer: {
    marginTop: theme.marginBase * 2,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
  },
}));

export const UpsellSupportSeveralSelection = ({
  sessionId,
  className,
}: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { data: allOffers } = useOffers();
  const { data: session } = useSession(sessionId);
  const { data: planOffers } = usePlanOffers(session?.planId);
  const { mutateAsync: updateSupportOffer } = useAddOfferToPlan(
    session?.planId,
  );
  const { data: plan } = usePlan(session?.planId);
  const { mutate: sendAction } = useSendAction('unlock_support_feature');
  const [selected, setSelected, closeModal] = useModalState<Offer | undefined>(
    undefined,
  );

  const availableOffers = useMemo(() => {
    if (!session || !allOffers) return [];
    return filterOffers(
      { ...session, priceThierId: plan?.priceThierId },
      allOffers,
    );
  }, [session, allOffers, plan]);

  const alreadySelectedProducts = useMemo(() => {
    const targetingOffer = planOffers?.find(
      (o) => o.type === OfferType.TARGETING,
    );
    if (!targetingOffer) {
      return [];
    }
    return targetingOffer.products.map((p) => p.product) as Product[];
  }, [planOffers]);

  const supportOffers = useMemo(() => {
    const currentOffer = planOffers?.find((o) => o.type === OfferType.SUPPORT);
    const currentPrice = currentOffer
      ? getSupportOfferPrice(currentOffer, alreadySelectedProducts)
      : 0;
    return availableOffers?.filter((o) => {
      if (o.type !== OfferType.SUPPORT) {
        return false;
      }
      if (o.requireCallToSales) {
        return false;
      }
      if (currentOffer?.id === o.id) {
        return false;
      }
      if (o.requireCallToSales) {
        return false;
      }
      const price = getSupportOfferPrice(o, alreadySelectedProducts);
      if (price <= currentPrice) {
        return false;
      }
      return true;
    });
  }, [availableOffers, planOffers, alreadySelectedProducts]);

  const onSubmit = useCallback(
    async (sel?: Offer) => {
      if (!sel) return;
      await updateSupportOffer({
        offerId: sel.id,
      });
      sendAction({
        offer: sel,
        session,
      });
      closeModal();
    },
    [updateSupportOffer, selected, closeModal, sendAction, session],
  );
  const onSubmitClick = useCallback(
    () => onSubmit(selected),
    [onSubmit, selected],
  );

  const onSelect = useCallback(
    (offer: Offer) => {
      if (session?.status === SessionStatus.DRAFT) {
        onSubmit(offer);
      } else {
        setSelected(offer);
      }
    },
    [setSelected, onSubmit, session],
  );

  const selectedName = selected?.name;

  const selectedPrice = useMemo(
    () =>
      selected ? getSupportOfferPrice(selected, alreadySelectedProducts) : 0,
    [selected, alreadySelectedProducts],
  );

  if (!supportOffers.length) {
    return null;
  }

  return (
    <div className={classNames(className, classes.container)}>
      <h5 className={classes.title}>{t('editSession.review.upsell.title')}</h5>
      <p className={classes.description}>
        {t('editSession.review.upsell.description')}
      </p>
      <div className={classes.suportContainer}>
        {supportOffers.map((offer) => (
          <CompactSupportOfferBlock
            offer={offer}
            alreadySelectedProducts={alreadySelectedProducts}
            onClick={onSelect}
          />
        ))}
      </div>
      <CenteredModal
        title={t('editSession.review.upsell.confirmTitle')}
        isOpen={!!selected}
        onRequestClose={closeModal}
        buttons={[
          <Button
            onClick={closeModal}
            text={t('editSession.review.upsell.cancel')}
            key="cancel"
            type="button"
            color="red"
            variant="transparent"
          />,
          <Button
            text={t('editSession.review.upsell.confirm')}
            onClick={onSubmitClick}
            key="confirm"
            type="button"
          />,
        ]}
      >
        <Trans
          i18nKey="editSession.review.upsell.confirmDescription"
          values={{ name: selectedName, price: selectedPrice }}
          components={{
            p: <p className={classes.modalText} />,
          }}
        />
      </CenteredModal>
    </div>
  );
};
